import { Stack } from '@mui/material';
import { ReportStatuses } from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import { ReportStatusTagSelect, ReportStatusTagSelectProps } from './ReportStatusTagSelect';

type ReportStatusExpandedSelectProps = PropsWithChildren<{
  selectedStatuses: ReportStatuses[];
  onApply: (v: ReportStatuses[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;

const OPTIONS = [ReportStatuses.Published, ReportStatuses.Unpublished, ReportStatuses.Draft];

export const ReportStatusExpandedSelect: FC<ReportStatusExpandedSelectProps> = ({
  selectedStatuses: initialSelectedStatuses,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedStatuses, setSelectedStatuses] =
    useState<ReportStatuses[]>(initialSelectedStatuses);

  const toggleStatuses = useCallback((v: ReportStatuses[]) => {
    setSelectedStatuses((statuses) => toggleMultipleValueArrayProperty(statuses, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedStatuses);
    onClose();
  }, [selectedStatuses, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderReportStatusTags({
            statuses: OPTIONS,
            tagProps: (status) => ({
              variant: selectedStatuses.includes(status) ? 'filled' : undefined,
            }),
            onClick: (status) => toggleStatuses([status]),
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [handleApply, onClear, toggleStatuses, selectedStatuses]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedStatuses.length > 0}
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
    >
      {renderReportStatusTags({
        tagProps: { size: 'small' },
        statuses: selectedStatuses,
        onDelete: (status) => toggleStatuses([status]),
      })}
    </ExpandedSelect>
  );
};

type RenderReportStatusTagsParams = {
  statuses: ReportStatuses[];
  onDelete?: (v: ReportStatuses) => void;
  onClick?: (v: ReportStatuses) => void;
  tagProps?:
    | Omit<ReportStatusTagSelectProps, 'status'>
    | ((v: ReportStatuses) => Omit<ReportStatusTagSelectProps, 'status'>);
};

export const renderReportStatusTags = ({
  statuses,
  onDelete,
  onClick,
  tagProps,
}: RenderReportStatusTagsParams) =>
  statuses.map((v) => (
    <ReportStatusTagSelect
      key={v}
      status={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
